import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { confirmDialog } from "../../../dialogs";
import { showToast } from "../../../toast";
import { loadAlbums } from "../../../albumsApi";
import { deleteMediaApi, loadGalleryData } from "../../../galleryApi";
import { getUploadBridge } from "../../../uploadBridge";
import { setWith, setWithout } from "../../../utils";
import { safeDateMs } from "../../../sortLogic";
import { GalleryCard } from "./GalleryCard";
import { GalleryListItem } from "./GalleryListItem";
import type { MediaItem } from "../../../types";

// "New since last visit" - a plain localStorage timestamp, not a server
// round trip (nothing to sync between two accounts here: each person's
// own sense of "have I seen this" is local to their own browser). Read
// once per app session (GalleryGrid stays mounted for the whole session,
// per AppRoot's "unconditionally mounted" convention) and deliberately
// NOT advanced again until MARK_VISITED_DELAY_MS after that first read -
// otherwise an item the other person adds while you're already looking at
// the gallery would immediately stop counting as "new" the moment
// loadGalleryData() re-fetches, which defeats the point.
const LAST_VISIT_KEY = "gallery-last-visit";
const MARK_VISITED_DELAY_MS = 3000;

function getLastVisitMs(): number {
  const raw = localStorage.getItem(LAST_VISIT_KEY);
  const ms = raw ? Number(raw) : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function markVisitedNow(): void {
  localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
}

function dateGroupLabel(ms: number): string {
  if (!ms) return "Unknown date";
  const d = new Date(ms);
  const now = new Date();
  const startOfDay = (dt: Date) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

async function handleDeleteMedia(filename: string) {
  const ok = await confirmDialog({
    title: "Delete this media?",
    message: `Delete "${filename.replace(".enc", "")}"? This cannot be undone.`,
    confirmLabel: "Delete",
  });
  if (!ok) return;

  const result = await deleteMediaApi(filename);
  if (!result.ok) {
    showToast(result.error, "error");
    return;
  }
  await loadAlbums();
  await loadGalleryData();
}

function GallerySkeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <div className="col-6 col-sm-4 col-md-4 col-lg-3" key={i}>
          <div className="skeleton-card" />
        </div>
      ))}
    </>
  );
}

function EmptyState() {
  // Adds directly into whatever's currently open (a specific album, or
  // "all") - the exact same bridge call AlbumHeroHeader.tsx's own "Add"
  // quick action uses, so this needs no album-picker detour. Previously
  // targeted a #uploadBtn element that no longer exists anywhere in the
  // DOM (a stale reference from before the bottom-nav/Add-page rework),
  // making this a silent no-op - fixed as part of touching this state.
  return (
    <>
      <i className="bi bi-images d-block mb-2 text-pink" style={{ fontSize: "2.25rem", opacity: 0.5 }} />
      <div className="fw-semibold mb-1">No memories yet</div>
      <div className="small muted mb-3">Add your first photo or video to get started.</div>
      <button
        type="button"
        className="btn btn-pink btn-sm"
        onClick={() => getUploadBridge()?.openFilePickerForAlbum(appState.currentAlbum)}
      >
        <i className="bi bi-plus-lg me-1" />
        Add Media
      </button>
    </>
  );
}

function NoResultsState({ favoritesOnly }: { favoritesOnly: boolean }) {
  if (favoritesOnly) {
    return (
      <>
        <i className="bi bi-heart d-block mb-2 text-pink" style={{ fontSize: "2.25rem", opacity: 0.5 }} />
        <div className="fw-semibold mb-1">No favorites yet</div>
        <div className="small muted">Tap the heart on a photo or video to favorite it.</div>
      </>
    );
  }
  return (
    <>
      <i className="bi bi-search d-block mb-2 text-pink" style={{ fontSize: "2.25rem", opacity: 0.5 }} />
      <div className="fw-semibold mb-1">No matches</div>
      <div className="small muted">Try a different search term.</div>
    </>
  );
}

interface BodyProps {
  visible: Array<{ m: MediaItem; index: number }>;
  grouping: boolean;
  favoritesSet: Set<string>;
  selectedFilenames: Set<string>;
  selectMode: boolean;
  lastVisitMs: number;
  onView: (index: number) => void;
  onToggleSelect: (filename: string) => void;
}

function GalleryGridBody({ visible, grouping, favoritesSet, selectedFilenames, selectMode, lastVisitMs, onView, onToggleSelect }: BodyProps) {
  let lastGroup: string | null = null;
  return (
    <>
      {visible.map(({ m, index }) => {
        const nodes = [];
        if (grouping) {
          const label = dateGroupLabel(safeDateMs(m));
          if (label !== lastGroup) {
            lastGroup = label;
            nodes.push(
              <div className="col-12 gallery-date-header" key={`hdr-${label}-${index}`}>
                {label}
              </div>,
            );
          }
        }
        nodes.push(
          <GalleryCard
            key={m.filename}
            item={m}
            isFav={favoritesSet.has(m.filename)}
            isSelected={selectedFilenames.has(m.filename)}
            selectMode={selectMode}
            isNew={lastVisitMs > 0 && safeDateMs(m) > lastVisitMs}
            onView={() => onView(index)}
            onToggleSelect={() => onToggleSelect(m.filename)}
            onDelete={() => handleDeleteMedia(m.filename)}
          />,
        );
        return nodes;
      })}
    </>
  );
}

function GalleryListBody({ visible, grouping, favoritesSet, selectedFilenames, selectMode, lastVisitMs, onView, onToggleSelect }: BodyProps) {
  let lastGroup: string | null = null;
  return (
    <div className="list-group">
      {visible.map(({ m, index }) => {
        const nodes = [];
        if (grouping) {
          const label = dateGroupLabel(safeDateMs(m));
          if (label !== lastGroup) {
            lastGroup = label;
            nodes.push(
              <div className="gallery-date-header" key={`hdr-${label}-${index}`}>
                {label}
              </div>,
            );
          }
        }
        nodes.push(
          <GalleryListItem
            key={m.filename}
            item={m}
            isFav={favoritesSet.has(m.filename)}
            isSelected={selectedFilenames.has(m.filename)}
            selectMode={selectMode}
            isNew={lastVisitMs > 0 && safeDateMs(m) > lastVisitMs}
            onView={() => onView(index)}
            onToggleSelect={() => onToggleSelect(m.filename)}
            onDelete={() => handleDeleteMedia(m.filename)}
          />,
        );
        return nodes;
      })}
    </div>
  );
}

// Portals into the existing #galleryGrid/#galleryHint containers - title/
// subtitle/back-button and every header control now live in
// AlbumHeroHeader.tsx, reading appState directly rather than this
// component imperatively pushing text into static DOM nodes.
export function GalleryGrid() {
  const media = useAppState((s) => s.media);
  const favoritesSet = useAppState((s) => s.favoritesSet);
  const selectedFilenames = useAppState((s) => s.selectedFilenames);
  const selectMode = useAppState((s) => s.selectMode);
  const viewMode = useAppState((s) => s.viewMode);
  const sortKey = useAppState((s) => s.sortState.key);
  const searchQuery = useAppState((s) => s.searchQuery);
  const favoritesOnly = useAppState((s) => s.favoritesOnly);
  const galleryLoading = useAppState((s) => s.galleryLoading);

  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return media
      .map((m, index) => ({ m, index }))
      .filter(({ m }) => {
        if (favoritesOnly && !favoritesSet.has(m.filename)) return false;
        if (!q) return true;
        return (m.filename || "").toLowerCase().includes(q);
      });
  }, [media, searchQuery, favoritesOnly, favoritesSet]);

  // Distinguishes "still loading, nothing to show yet" (skeleton) from
  // "genuinely has nothing" (empty-state hint) - previously indistinguishable,
  // so switching albums or the first load always flashed "No media yet"
  // before the real fetch had a chance to land.
  const showSkeleton = galleryLoading && !media.length;
  const showEmpty = !showSkeleton && !media.length;
  const showNoResults = !showSkeleton && !showEmpty && !visible.length;
  const grouping = sortKey === "date";

  // Captured once for this whole app session (see the constants above) -
  // not re-read on every render, so it doesn't move just because
  // loadGalleryData() re-fetches while you're still looking.
  const lastVisitMs = useRef(getLastVisitMs()).current;

  useEffect(() => {
    const timer = setTimeout(markVisitedNow, MARK_VISITED_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const gridPortalTarget = getVanillaEl<HTMLDivElement>("galleryGrid");
  const hintPortalTarget = getVanillaEl<HTMLDivElement>("galleryHint");

  useEffect(() => {
    hintPortalTarget?.classList.toggle("d-none", !(showEmpty || showNoResults));
  }, [hintPortalTarget, showEmpty, showNoResults]);

  useEffect(() => {
    if (!gridPortalTarget) return;
    gridPortalTarget.classList.toggle("row", viewMode !== "list");
    gridPortalTarget.classList.toggle("g-3", viewMode !== "list");
  }, [gridPortalTarget, viewMode]);

  function onView(index: number) {
    appState.currentMediaIndex = index;
    notifyStateChange();
  }

  function onToggleSelect(filename: string) {
    appState.selectedFilenames = appState.selectedFilenames.has(filename)
      ? setWithout(appState.selectedFilenames, filename)
      : setWith(appState.selectedFilenames, filename);
    notifyStateChange();
  }

  if (!gridPortalTarget) return null;

  const gridContent = showSkeleton ? (
    <GallerySkeleton />
  ) : showEmpty || showNoResults ? null : viewMode === "list" ? (
    <GalleryListBody
      visible={visible}
      grouping={grouping}
      favoritesSet={favoritesSet}
      selectedFilenames={selectedFilenames}
      selectMode={selectMode}
      lastVisitMs={lastVisitMs}
      onView={onView}
      onToggleSelect={onToggleSelect}
    />
  ) : (
    <GalleryGridBody
      visible={visible}
      grouping={grouping}
      favoritesSet={favoritesSet}
      selectedFilenames={selectedFilenames}
      selectMode={selectMode}
      lastVisitMs={lastVisitMs}
      onView={onView}
      onToggleSelect={onToggleSelect}
    />
  );

  const hintContent = showEmpty ? <EmptyState /> : showNoResults ? <NoResultsState favoritesOnly={favoritesOnly} /> : null;

  return (
    <>
      {createPortal(gridContent, gridPortalTarget)}
      {hintPortalTarget && createPortal(hintContent, hintPortalTarget)}
    </>
  );
}
